import { type FC, useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import {
  importPlanActionXLSX,
  PlanActionImportError,
  type ImportPhase,
  type ImportProgress,
  type PlanActionImportResult,
} from '@/services/importPlanAction';

interface ImportPlanActionButtonProps {
  workspaceId: string;
  onImported: (result: PlanActionImportResult) => void;
}

const PHASE_LABELS: Record<ImportPhase, string> = {
  parsing: 'Lecture du fichier…',
  canvas: 'Création du canvas…',
  visions: 'Création des visions (thèmes)',
  objectives: 'Création des objectifs',
  actions: 'Création des actions',
  stakeholders: 'Création des parties prenantes',
  links: 'Création des liaisons',
  done: 'Terminé',
};

export const ImportPlanActionButton: FC<ImportPlanActionButtonProps> = ({
  workspaceId,
  onImported,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);

  const handleClick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    setError(null);
    setProgress({ phase: 'parsing', current: 0, total: 1, label: file.name });
    try {
      const result = await importPlanActionXLSX(file, workspaceId, (p) => setProgress(p));
      onImported(result);
    } catch (err) {
      const msg =
        err instanceof PlanActionImportError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Échec de l'import";
      setError(msg);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <Button
        onClick={handleClick}
        disabled={busy}
        variant="secondary"
        title="Créer un canvas depuis un export XLSX de plan d'actions (AgeVal)"
      >
        {busy ? 'Import XLSX…' : "Import plan d'action"}
      </Button>
      {error && (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: '#EF4444',
            marginLeft: 'var(--spacing-sm)',
          }}
        >
          {error}
        </span>
      )}
      {busy && progress && <ImportProgressModal progress={progress} />}
    </>
  );
};

const ImportProgressModal: FC<{ progress: ImportProgress }> = ({ progress }) => {
  const percent =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 'min(440px, 92vw)',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--spacing-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-md)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 700,
            color: 'var(--color-text)',
          }}
        >
          Import du plan d'action en cours…
        </div>

        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          {PHASE_LABELS[progress.phase]}
          {progress.phase !== 'parsing' &&
            progress.phase !== 'canvas' &&
            progress.phase !== 'done' &&
            progress.total > 0 && (
              <span>
                {' '}
                — {progress.current} / {progress.total}
              </span>
            )}
        </div>

        <div
          style={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'var(--color-border)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: '100%',
              backgroundColor: 'var(--color-primary, #3B82F6)',
              transition: 'width 120ms ease',
            }}
          />
        </div>

        {progress.label && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--color-text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontStyle: 'italic',
            }}
          >
            « {progress.label} »
          </div>
        )}

        <div
          style={{
            fontSize: 10,
            color: 'var(--color-text-muted)',
            textAlign: 'right',
          }}
        >
          {percent}%
        </div>
      </div>
    </div>
  );
};

export default ImportPlanActionButton;
